// src/Services/categoryService.ts
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

export interface Category {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  parentId: string | null;
  isActive: boolean;
  isStarred?: boolean; // Add this property
  sortOrder: number;
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

const CATEGORIES_COLLECTION = 'categories';

export class CategoryService {
  // Get all categories
  static async getAllCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(categoriesRef, orderBy('sortOrder', 'asc'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching all categories:', error);
      throw new Error('Failed to fetch categories');
    }
  }

  // Get only active categories
  static async getActiveCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef, 
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching active categories:', error);
      throw new Error('Failed to fetch active categories');
    }
  }

  // Get parent categories (where parentId is null)
  static async getParentCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef, 
        where('parentId', '==', null),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      throw new Error('Failed to fetch parent categories');
    }
  }

  // Get active parent categories
  static async getActiveParentCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef, 
        where('parentId', '==', null),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching active parent categories:', error);
      throw new Error('Failed to fetch active parent categories');
    }
  }

  // Get starred active parent categories
  static async getStarredActiveParentCategories(): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef, 
        where('parentId', '==', null),
        where('isActive', '==', true),
        where('isStarred', '==', true),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching starred active parent categories:', error);
      throw new Error('Failed to fetch starred active parent categories');
    }
  }

  // Get categories for banner display (max 6)
  // Returns starred categories first, then fills with non-starred if needed
  static async getBannerCategories(maxCount: number = 6): Promise<Category[]> {
    try {
      // First, get starred categories
      const starredCategories = await this.getStarredActiveParentCategories();
      
      // If we have 6 or more starred categories, return only the first 6
      if (starredCategories.length >= maxCount) {
        return starredCategories.slice(0, maxCount);
      }
      
      // If we have fewer than 6 starred categories, get non-starred to fill the gap
      const remainingCount = maxCount - starredCategories.length;
      
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef,
        where('parentId', '==', null),
        where('isActive', '==', true),
        where('isStarred', '!=', true), // Get non-starred categories
        orderBy('isStarred', 'asc'), // Required for != query
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc'),
        limit(remainingCount)
      );
      
      const querySnapshot = await getDocs(q);
      const nonStarredCategories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
      
      // Combine starred and non-starred categories
      return [...starredCategories, ...nonStarredCategories];
    } catch (error) {
      console.error('Error fetching banner categories:', error);
      // Fallback: just get the first 6 active parent categories
      try {
        const categoriesRef = collection(db, CATEGORIES_COLLECTION);
        const q = query(
          categoriesRef,
          where('parentId', '==', null),
          where('isActive', '==', true),
          orderBy('sortOrder', 'asc'),
          orderBy('name', 'asc'),
          limit(maxCount)
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Category));
      } catch (fallbackError) {
        console.error('Error in fallback fetch:', fallbackError);
        throw new Error('Failed to fetch banner categories');
      }
    }
  }

  // Get subcategories by parent ID
  static async getSubcategoriesByParentId(parentId: string): Promise<Category[]> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const q = query(
        categoriesRef, 
        where('parentId', '==', parentId),
        orderBy('sortOrder', 'asc'),
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Category));
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw new Error('Failed to fetch subcategories');
    }
  }

  // Get categories with their subcategories
  static async getCategoriesWithSubcategories(): Promise<(Category & { subcategories: Category[] })[]> {
    try {
      const allCategories = await this.getAllCategories();
      const parentCategories = allCategories.filter(cat => cat.parentId === null);
      
      return parentCategories.map(parent => ({
        ...parent,
        subcategories: allCategories.filter(cat => cat.parentId === parent.id)
      }));
    } catch (error) {
      console.error('Error fetching categories with subcategories:', error);
      throw new Error('Failed to fetch categories with subcategories');
    }
  }

  // Get active categories with their active subcategories
  static async getActiveCategoriesWithSubcategories(): Promise<(Category & { subcategories: Category[] })[]> {
    try {
      const activeCategories = await this.getActiveCategories();
      const activeParentCategories = activeCategories.filter(cat => cat.parentId === null);
      
      return activeParentCategories.map(parent => ({
        ...parent,
        subcategories: activeCategories.filter(cat => cat.parentId === parent.id)
      }));
    } catch (error) {
      console.error('Error fetching active categories with subcategories:', error);
      throw new Error('Failed to fetch active categories with subcategories');
    }
  }

  // Get a single category by ID
  static async getCategoryById(id: string): Promise<Category | null> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      const categoryDoc = await getDoc(categoryRef);
      
      if (categoryDoc.exists()) {
        return {
          id: categoryDoc.id,
          ...categoryDoc.data()
        } as Category;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching category by ID:', error);
      throw new Error('Failed to fetch category');
    }
  }

  // Create a new category
  static async createCategory(
    category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const categoriesRef = collection(db, CATEGORIES_COLLECTION);
      const docRef = await addDoc(categoriesRef, {
        ...category,
        isStarred: category.isStarred || false, // Ensure isStarred is set
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error('Failed to create category');
    }
  }

  // Update a category
  static async updateCategory(
    id: string,
    category: Partial<Omit<Category, 'id' | 'createdAt' | 'createdBy'>>
  ): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await updateDoc(categoryRef, {
        ...category,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating category:', error);
      throw new Error('Failed to update category');
    }
  }

  // Delete a category
  static async deleteCategory(id: string): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await deleteDoc(categoryRef);
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category');
    }
  }

  // Deactivate a category (soft delete)
  static async deactivateCategory(id: string): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await updateDoc(categoryRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error deactivating category:', error);
      throw new Error('Failed to deactivate category');
    }
  }

  // Activate a category
  static async activateCategory(id: string): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await updateDoc(categoryRef, {
        isActive: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error activating category:', error);
      throw new Error('Failed to activate category');
    }
  }

  // Toggle starred status
  static async toggleStarredCategory(id: string, isStarred: boolean): Promise<void> {
    try {
      const categoryRef = doc(db, CATEGORIES_COLLECTION, id);
      await updateDoc(categoryRef, {
        isStarred,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error toggling starred category:', error);
      throw new Error('Failed to toggle starred status');
    }
  }
}